/**
 * `help/HelpMenu.tsx` — the top bar's small **?** Help button.
 *
 * The founder, 7 Oct 2026: the guided view should be a tiny (?) button
 * that starts it again and leads to guides on each part. The menu holds:
 *
 *   - **Show me around** (leads and co-leads) — the spotlight tour
 *     (`tour/useTour.ts openTour()`); it used to live in the account menu;
 *   - **Guides** — one row per in-app guide (`guides.ts`); a row opens
 *     that guide in a side drawer the page stays usable behind.
 *
 * Other surfaces open a guide with `openGuide(id)` (`useHelpGuide.ts`).
 * The inner `<HelpMenuList/>` is exported so tests can drive the rows
 * without Radix Dropdown's pointer events (as `<ThemeOptionList/>` is).
 */
import { CircleHelp, Compass, Map as MapIcon } from "lucide-react";
import { type ReactNode, useCallback, useRef, useState } from "react";

import { isGuidedDemo } from "../../lib/demoSession";
import { requestDemoGuideChooser } from "../demo/guide/events";
import { openTour } from "../tour/useTour";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
} from "../ui";
import { HelpGuideDrawer } from "./HelpGuideDrawer";
import { HELP_GUIDES, findGuide } from "./guides";
import { useGuideOpenRequests } from "./useHelpGuide";

export interface HelpMenuProps {
  /** Offer "Show me around" (the spotlight tour; leads and co-leads). */
  showTour: boolean;
}

export function HelpMenu({ showTour }: HelpMenuProps) {
  const [openGuideId, setOpenGuideId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const show = useCallback((id: string) => {
    if (findGuide(id)) setOpenGuideId(id);
  }, []);

  // A guide asked for from elsewhere (a section's "?") opens here.
  useGuideOpenRequests(show);

  // From the menu: let the dropdown finish closing (it hands focus back
  // to its trigger) before the drawer opens and takes focus.
  const pickGuide = useCallback((id: string) => window.setTimeout(() => show(id), 0), [show]);

  return (
    <>
      <DropdownMenu>
        <Tooltip content="Help">
          <DropdownMenuTrigger asChild>
            <Button
              ref={triggerRef}
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Help"
              data-testid="help-menu-trigger"
              data-tour="help"
              className="h-8 w-8 text-fg-subtle hover:text-fg"
            >
              <CircleHelp className="h-4 w-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
        </Tooltip>
        <DropdownMenuContent align="end" className="min-w-[16rem]">
          <HelpMenuList showTour={showTour} onOpenGuide={pickGuide} />
        </DropdownMenuContent>
      </DropdownMenu>
      <HelpGuideDrawer
        guide={findGuide(openGuideId) ?? null}
        onClose={() => setOpenGuideId(null)}
        fallbackFocusRef={triggerRef}
      />
    </>
  );
}

export interface HelpMenuListProps {
  showTour: boolean;
  /** "Show the guide" (#2824): the guided demo's chapters. Default: only in a guided demo. */
  showGuide?: boolean;
  onOpenGuide: (id: string) => void;
  /** Rows as Radix menu items (inside `<HelpMenu/>`'s dropdown, the
   *  default) or as plain buttons (tests, or a page that lists the guides). */
  inMenu?: boolean;
}

/** The menu's rows: Show me around (leads), then the guides. */
export function HelpMenuList({
  showTour: tourAllowed,
  showGuide = isGuidedDemo(),
  onOpenGuide,
  inMenu = true,
}: HelpMenuListProps) {
  // #2824 - in a demo the guide replaces the spotlight tour.
  const showTour = tourAllowed && !isGuidedDemo();
  if (!inMenu) {
    return (
      <div className="flex flex-col gap-0.5" data-testid="help-menu-list">
        {showGuide ? (
          <PlainRow testid="help-show-guide" onPick={() => requestDemoGuideChooser()}>
            <MapIcon className="h-4 w-4" aria-hidden="true" />
            Show the guide
          </PlainRow>
        ) : null}
        {showTour ? (
          <PlainRow testid="help-show-tour" onPick={() => openTour()}>
            <Compass className="h-4 w-4" aria-hidden="true" />
            Show me around
          </PlainRow>
        ) : null}
        <p className="px-3 py-2">
          <GuidesLabel />
        </p>
        {HELP_GUIDES.map((guide) => (
          <PlainRow
            key={guide.id}
            testid={`help-guide-${guide.id}`}
            onPick={() => onOpenGuide(guide.id)}
          >
            <GuideRowText title={guide.title} summary={guide.summary} />
          </PlainRow>
        ))}
      </div>
    );
  }
  return (
    <>
      {showGuide ? (
        <>
          {/* The chooser opens once the menu has closed and returned focus. */}
          <DropdownMenuItem
            onSelect={() => window.setTimeout(requestDemoGuideChooser, 60)}
            data-testid="help-show-guide"
          >
            <MapIcon className="h-4 w-4" aria-hidden="true" />
            Show the guide
          </DropdownMenuItem>
          {showTour ? null : <DropdownMenuSeparator />}
        </>
      ) : null}
      {showTour ? (
        <>
          <DropdownMenuItem onSelect={() => openTour()} data-testid="help-show-tour">
            <Compass className="h-4 w-4" aria-hidden="true" />
            Show me around
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      ) : null}
      <DropdownMenuLabel>
        <GuidesLabel />
      </DropdownMenuLabel>
      {HELP_GUIDES.map((guide) => (
        <DropdownMenuItem
          key={guide.id}
          onSelect={() => onOpenGuide(guide.id)}
          data-testid={`help-guide-${guide.id}`}
        >
          <GuideRowText title={guide.title} summary={guide.summary} />
        </DropdownMenuItem>
      ))}
    </>
  );
}

function GuidesLabel() {
  return <span className="text-[10px] uppercase tracking-wider text-fg-subtle">Guides</span>;
}

function GuideRowText({ title, summary }: { title: string; summary: string }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="leading-tight">{title}</span>
      <span className="text-xs leading-tight text-fg-subtle">{summary}</span>
    </span>
  );
}

function PlainRow({
  testid,
  onPick,
  children,
}: {
  testid: string;
  onPick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onPick}
      className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm text-fg-muted transition-colors hover:bg-bg-subtle hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {children}
    </button>
  );
}
